import { useEffect, useRef, useState } from 'react';

import { TabProps } from './../';
import PersonaTable from './PersonaTable';
import PersonaForm from './PersonaForm';
import styles from './Persona.module.css';
import { Toast } from 'admin/shared-components/Toast/Toast';
import { ViewContainer } from 'admin/shared-components/View/ViewContainer/ViewContainer';
import { PersonaRowData, PersonaToast } from 'admin/types';
import { useAppSelector } from 'redux-stores';
import { DotsIndicator } from 'common/components';
import { useGetPersonasStatusQuery } from 'redux-stores/admin/organizationsApi';
import { mobxInjectSelect } from 'common/utils';

export type PersonaFormType = 'title' | 'scim';

export type PersonaFormState = {
  display: boolean;
  type: PersonaFormType;
};

type MobxProps = {
  startSettingsTimeTracking: () => void;
  stopSettingsTimeTracking: () => void;
};

const PersonaTab = ({
  onClose,
  startSettingsTimeTracking,
  stopSettingsTimeTracking,
}: TabProps & MobxProps) => {
  const { selectedOrganizationId } = useAppSelector((state) => ({
    selectedOrganizationId: state.admin.selectedOrganizationId,
  }));
  const isInitialMount = useRef(true);
  const [pollingInterval, setPollingInterval] = useState(1000 * 90);
  const {
    data: personaStatus,
    isLoading: isPersonaStatusLoading,
    error: personaStatusError,
  } = useGetPersonasStatusQuery(
    { orgId: selectedOrganizationId },
    {
      pollingInterval: pollingInterval,
      refetchOnMountOrArgChange: true,
    }
  );
  useEffect(() => {
    if (
      personaStatus?.persona_titles_status &&
      personaStatus.persona_titles_status === 'processing'
    ) {
      setPollingInterval(1000 * 90);
    } else {
      setPollingInterval(0);
    }
  }, [personaStatus]);

  useEffect(() => {
    if (isInitialMount.current) {
      if (selectedOrganizationId) {
        startSettingsTimeTracking();
      }
      isInitialMount.current = false;
    }
    return () => {
      stopSettingsTimeTracking();
    };
  }, [selectedOrganizationId, startSettingsTimeTracking, stopSettingsTimeTracking]);

  const [personaFormState, setPersonaFormState] = useState<PersonaFormState>({
    display: false,
    type: 'title',
  });
  const [editMode, setEditMode] = useState(true);
  const [toastMessageData, setToastMessageData] = useState<PersonaToast | undefined>();
  const [selectedPersona, setSelectedPersona] = useState<PersonaRowData | undefined>();

  if (isPersonaStatusLoading) {
    return (
      <ViewContainer
        {...{
          header: 'Personas',
          subHeader: '',
          onClose,
        }}
      >
        <div className={styles.messageContainer}>
          <DotsIndicator />
        </div>
      </ViewContainer>
    );
  } else if (personaStatus?.persona_titles_status === 'processing') {
    return (
      <ViewContainer
        {...{
          header: 'Personas',
          subHeader: '',
          onClose,
        }}
      >
        <div className={styles.messageContainer}>
          <h2>Enabling Personas.</h2>
          <DotsIndicator />
          <p>Please give us a few minutes to process your data and enable the Persona feature.</p>
        </div>
      </ViewContainer>
    );
  } else if (personaStatus?.persona_titles_status === 'failed') {
    return (
      <ViewContainer
        {...{
          header: 'Personas',
          subHeader: '',
          onClose,
        }}
      >
        <div className={styles.messageContainer}>
          <h2>An error occurred.</h2>
          <p>
            An error occurred while enabling Personas, please reach out to TigerConnect for
            assistance.
          </p>
        </div>
      </ViewContainer>
    );
  } else if (personaStatusError) {
    return (
      <ViewContainer
        {...{
          header: 'Personas',
          subHeader: '',
          onClose,
        }}
      >
        <div className={styles.messageContainer}>
          <h2>An error occurred.</h2>
          <p>An error occurred while fetching data, please refresh the page.</p>
        </div>
      </ViewContainer>
    );
  } else {
    return (
      <ViewContainer
        {...{
          header: 'Personas',
          subHeader: '',
          onClose,
        }}
      >
        <>
          <Toast
            message={toastMessageData?.message || ''}
            type={toastMessageData?.type || 'SUCCESS'}
            open={!!toastMessageData}
            onClose={() => setToastMessageData(undefined)}
            autoHideDuration={2000}
          />
          {!personaFormState.display ? (
            <PersonaTable
              setEditMode={setEditMode}
              showPersonaForm={personaFormState.display}
              setPersonaFormState={setPersonaFormState}
              setSelectedPersona={setSelectedPersona}
              setToastMessageData={setToastMessageData}
            />
          ) : (
            <PersonaForm
              editMode={editMode}
              setEditMode={setEditMode}
              setPersonaFormState={setPersonaFormState}
              formType={personaFormState.type}
              selectedPersona={selectedPersona}
              setSelectedPersona={setSelectedPersona}
              setToastMessageData={setToastMessageData}
            />
          )}
        </>
      </ViewContainer>
    );
  }
};

export default mobxInjectSelect<TabProps, MobxProps>({
  personaStore: ['startSettingsTimeTracking', 'stopSettingsTimeTracking'],
})(PersonaTab);
